/**
 * CourseSlidesPanel.js - PDF Slide Viewer Panel using react-pdf
 * Displays course slides from slides_pdf/{class_section}-{university}/
 * Page-by-page navigation with slide list sidebar.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getCurriculumFolder } from '../App';
import {API_BASE_URL, PYTHON_INTERPRETER_URL, apiFetch, isDemoMode } from '../config';
import TurtleModal from './TurtleModal';
import CodeRunnerModal from './CodeRunnerModal';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const CourseSlidesPanel = ({ onClose, selectedSection }) => {
  const [pdfFiles, setPdfFiles] = useState([]);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(900);

  // Recompute folder whenever selectedSection prop changes (reads from localStorage which App.js keeps in sync)
  const folder = getCurriculumFolder();
  const username = localStorage.getItem('username') || 'default';

  // Covered pages: stored as Set of "filename-pN" keys
  const storageKey = `coveredSlides_${username}_${folder}`;
  const [coveredPages, setCoveredPages] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  const toggleCovered = (file, page) => {
    const key = `${file}-p${page}`;
    setCoveredPages(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      localStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  };

  const isPageCovered = (file, page) => coveredPages.has(`${file}-p${page}`);

  // === Scratch Pad state ===
  const [showScratchPad, setShowScratchPad] = useState(false);
  const [showTurtle, setShowTurtle] = useState(false);

  // === Memo Pad state (per-slide-page) ===
  const [showMemo, setShowMemo] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [memoPos, setMemoPos] = useState({ x: window.innerWidth - 520, y: 200 });
  const [memoSize, setMemoSize] = useState({ width: 400, height: 350 });
  const memoDragging = useRef(false);
  const memoResizing = useRef(false);
  const memoDragOffset = useRef({ x: 0, y: 0 });

  // === Explain Panel state ===
  const [showExplain, setShowExplain] = useState(false);
  const [explainContent, setExplainContent] = useState('');
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainPos, setExplainPos] = useState({ x: 60, y: 80 });
  const [explainSize, setExplainSize] = useState({ width: 520, height: 500 });
  const explainDragging = useRef(false);
  const explainResizing = useRef(false);
  const explainDragOffset = useRef({ x: 0, y: 0 });
  const explainCache = useRef({}); // cache per "file-pN"

  // === Quiz state (per explain) ===
  const [explainQuiz, setExplainQuiz] = useState([]); // array of { question, options[], correct, explanation }
  const [quizAnswers, setQuizAnswers] = useState({}); // { questionIndex: chosenIndex }

  // Extract text from PDF pages 1..currentPage and send to LLM
  const handleExplainSlide = async () => {
    if (!pdfUrl || !numPages) return;
    const cacheKey = `${selectedPdf.file}-p${currentPage}`;
    if (explainCache.current[cacheKey]) {
      const cached = explainCache.current[cacheKey];
      // Support old string-format cache entries
      const cachedExplanation = typeof cached === 'string' ? cached : cached.explanation;
      const cachedQuiz = typeof cached === 'string' ? [] : (cached.quiz || []);
      setExplainContent(cachedExplanation);
      setExplainQuiz(cachedQuiz);
      setQuizAnswers({});
      setShowExplain(true);
      return;
    }
    setShowExplain(true);
    setExplainLoading(true);
    setExplainContent('');

    // Demo mode: PDF viewing works, but AI explanation requires login
    if (isDemoMode()) {
      setExplainContent('🔒 AI Slide Explanation — Locked in Demo Mode\n\nWith a real account, the AI reads this slide and gives you a plain-English breakdown.\n\nFor example, for a slide on for loops it might say:\n\n  "This slide introduces the for loop. The syntax `for item in list:`\n   repeats the indented block once per item. With `range(5)`, the variable\n   i takes values 0, 1, 2, 3, 4 — giving you 5 iterations total."\n\nYou can still read all the slides in demo mode.\n\n✨ Sign up to unlock AI explanations!');
      setExplainLoading(false);
      return;
    }
    setExplainQuiz([]);
    setQuizAnswers({});
    try {
      // Load PDF and extract text from up to 15 pages (current + 14 previous for context)
      const startPage = Math.max(1, currentPage - 14);
      const fullPdfUrl = window.location.origin + pdfUrl;
      const loadingTask = pdfjs.getDocument(fullPdfUrl);
      let pdf;
      try {
        pdf = await loadingTask.promise;
      } catch (pdfErr) {
        setExplainContent('❌ Could not load PDF for text extraction: ' + pdfErr.message);
        setExplainLoading(false);
        return;
      }
      const pageTexts = [];
      for (let p = startPage; p <= currentPage; p++) {
        const page = await pdf.getPage(p);
        const textContent = await page.getTextContent();
        const text = textContent.items.map(item => item.str).join(' ');
        if (text.trim()) {
          pageTexts.push(`--- Page ${p} ---\n${text}`);
        }
      }
      if (pageTexts.length === 0) {
        setExplainContent('⚠️ No text could be extracted from this slide.');
        setExplainLoading(false);
        return;
      }
      const allText = pageTexts.join('\n\n');
      // Call GPT-4o Mini
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: 'You are a friendly Python programming tutor. Explain slide content clearly to a beginner student. Use simple language, give examples when helpful, and highlight key concepts. Format your response with clear sections using markdown-style formatting. IMPORTANT: Never use LaTeX notation like \\( \\) or \\[ \\] or \\text{} for math — always write math in plain text (e.g. "area = pi * radius^2" not "\\( \\text{area} = \\pi \\times \\text{radius}^2 \\)").\n\nAfter your explanation, add exactly the delimiter |||QUIZ||| on its own line, followed by a JSON array of exactly 3 multiple-choice questions about the slide content. Each question object must have: "question" (string), "options" (array of 4 strings like "A) ...", "B) ...", "C) ...", "D) ..."), "correct" (integer 0-3 for the correct option index), "explanation" (short string explaining why the answer is correct). Return ONLY valid JSON after the delimiter, no markdown code fences.'
            },
            {
              role: 'user',
              content: `Below is the slide content from a Python lecture chapter (pages ${startPage} to ${currentPage}). Please explain the content on page ${currentPage} (the current slide) to a student. Use the earlier pages only for context to make the explanation more coherent. Then provide 3 quiz questions after the |||QUIZ||| delimiter.\n\n${allText}`
            }
          ],
          temperature: 0.7,
          max_tokens: 2500
        })
      });
      const data = await response.json();
      if (data.choices && data.choices[0]) {
        const fullResponse = data.choices[0].message.content;
        // Split explanation and quiz — try multiple delimiter patterns
        let explanation = fullResponse;
        let quiz = [];

        // Helper: extract quiz array from parsed JSON (handles both array and {questions:[...]} object)
        const extractQuizArray = (parsed) => {
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].question) return parsed;
          if (parsed && typeof parsed === 'object' && Array.isArray(parsed.questions)) return parsed.questions;
          return null;
        };

        // Helper: clean JSON string from markdown fences
        const cleanJsonStr = (s) => s.replace(/^```json?\s*/i, '').replace(/\s*```\s*$/i, '').trim();

        // Try 1: Match |||QUIZ|||, ||| QUIZ |||, |||Quiz|||, or just ||| as delimiter
        const delimiterMatch = fullResponse.match(/\|{2,3}\s*(?:QUIZ)?\s*\|{2,3}/i);
        if (delimiterMatch) {
          const delimIdx = delimiterMatch.index;
          const beforeDelim = fullResponse.substring(0, delimIdx).trim();
          const afterDelim = fullResponse.substring(delimIdx + delimiterMatch[0].length).trim();
          if (afterDelim.length > 10) { // must have meaningful content after delimiter
            try {
              const parsed = JSON.parse(cleanJsonStr(afterDelim));
              const extracted = extractQuizArray(parsed);
              if (extracted) {
                quiz = extracted;
                explanation = beforeDelim;
              }
            } catch (parseErr) {
              console.warn('Quiz parse error after delimiter:', parseErr);
            }
          }
        }

        // Try 2: Find trailing JSON block (array or object with "questions" key)
        if (quiz.length === 0) {
          // Match a JSON object {...} or array [...] at the end that contains "question"
          const jsonBlockMatch = fullResponse.match(/([\[{][\s\S]*"question"[\s\S]*[\]}])\s*$/);
          if (jsonBlockMatch) {
            try {
              const parsed = JSON.parse(cleanJsonStr(jsonBlockMatch[1]));
              const extracted = extractQuizArray(parsed);
              if (extracted) {
                quiz = extracted;
                explanation = fullResponse.substring(0, jsonBlockMatch.index).trim();
              }
            } catch (e) { /* ignore */ }
          }
        }

        // Final cleanup: strip any leftover delimiter markers or stray ||| from explanation end
        explanation = explanation.replace(/\|{2,3}\s*(?:QUIZ)?\s*\|{2,3}\s*$/i, '').trim();
        explanation = explanation.replace(/\|{2,}\s*$/g, '').trim();

        setExplainContent(explanation);
        setExplainQuiz(quiz);
        setQuizAnswers({});
        explainCache.current[cacheKey] = { explanation, quiz };
      } else {
        setExplainContent('❌ Unexpected response from AI. Please try again.');
      }
    } catch (err) {
      setExplainContent('❌ Error: ' + err.message);
    } finally {
      setExplainLoading(false);
    }
  };

  // Build per-slide-page storage keys
  const getMemoKey = (file, page) => `slideMemo_${username}_${folder}_${file}-p${page}`;

  // Load memo when slide/page changes
  useEffect(() => {
    if (selectedPdf) {
      const savedMemo = localStorage.getItem(getMemoKey(selectedPdf.file, currentPage)) || '';
      setMemoText(savedMemo);
    }
  }, [selectedPdf, currentPage, folder, username]);

  // Auto-save memo text
  useEffect(() => {
    if (selectedPdf) {
      localStorage.setItem(getMemoKey(selectedPdf.file, currentPage), memoText);
    }
  }, [memoText, selectedPdf, currentPage]);

  // Dragging logic for memo and explain panels
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (memoDragging.current) {
        setMemoPos({
          x: Math.max(0, Math.min(window.innerWidth - 100, e.clientX - memoDragOffset.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - 40, e.clientY - memoDragOffset.current.y))
        });
      }
      if (memoResizing.current) {
        const dx = e.clientX - memoDragOffset.current.x;
        const dy = e.clientY - memoDragOffset.current.y;
        setMemoSize({
          width: Math.max(280, memoDragOffset.current.width + dx),
          height: Math.max(200, memoDragOffset.current.height + dy)
        });
      }
      if (explainDragging.current) {
        setExplainPos({
          x: Math.max(0, Math.min(window.innerWidth - 100, e.clientX - explainDragOffset.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - 40, e.clientY - explainDragOffset.current.y))
        });
      }
      if (explainResizing.current) {
        const dx = e.clientX - explainDragOffset.current.x;
        const dy = e.clientY - explainDragOffset.current.y;
        setExplainSize({
          width: Math.max(350, explainDragOffset.current.width + dx),
          height: Math.max(250, explainDragOffset.current.height + dy)
        });
      }
    };
    const handleMouseUp = () => {
      memoDragging.current = false;
      memoResizing.current = false;
      explainDragging.current = false;
      explainResizing.current = false;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Prevent background scrolling
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Measure container width for responsive PDF rendering
  const containerRef = useCallback((node) => {
    if (node) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width - 40); // padding
        }
      });
      resizeObserver.observe(node);
    }
  }, []);

  // Load the list of available PDF files
  useEffect(() => {
    const loadPdfList = async () => {
      setLoading(true);
      try {
        // Try manifest.json first
        let usedManifest = false;
        try {
          const response = await fetch(`/slides_pdf/${folder}/manifest.json`);
          if (response.ok) {
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              const data = await response.json();
              if (Array.isArray(data) && data.length > 0) {
                setPdfFiles(data);
                setSelectedPdf(data[0]);
                usedManifest = true;
              }
            }
          }
        } catch (e) { /* manifest not available */ }

        if (!usedManifest) {
          // Fallback: probe for PDFs
          const detected = [];
          for (let i = 1; i <= 30; i++) {
            const paddedNum = String(i).padStart(2, '0');
            const fileName = `${paddedNum}slide.pdf`;
            const checkRes = await fetch(`/slides_pdf/${folder}/${fileName}`, { method: 'HEAD' });
            if (checkRes.ok) {
              const ct = checkRes.headers.get('content-type') || '';
              if (ct.includes('pdf')) {
                detected.push({ name: `Slide ${i}`, file: fileName });
              }
            }
          }
          if (detected.length > 0) {
            setPdfFiles(detected);
            setSelectedPdf(detected[0]);
          } else {
            setError('No slide PDFs found. Add PDFs to: slides_pdf/' + folder + '/');
          }
        }
      } catch (err) {
        setError('Failed to load slides: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    loadPdfList();
  }, [folder]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= numPages) {
      setCurrentPage(page);
    }
  };

  const pdfUrl = selectedPdf ? `/slides_pdf/${folder}/${selectedPdf.file}` : null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#161b22',
        borderRadius: '12px',
        border: '1.5px solid rgba(255, 215, 102, 0.4)',
        width: '100%',
        maxWidth: '1400px',
        height: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #30363d',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <h2 style={{ margin: 0, color: '#86efac', fontSize: '24px' }}>
            📑 Course Slides
          </h2>
          <button onClick={onClose} style={{
            background: 'none',
            border: 'none',
            color: '#f0f6fc',
            fontSize: '24px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          onMouseOver={(e) => e.target.style.color = '#f85149'}
          onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
          >✕</button>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left sidebar - Slide list */}
          <div style={{
            width: '220px',
            borderRight: '1px solid #30363d',
            overflowY: 'auto',
            padding: '12px',
            flexShrink: 0,
            backgroundColor: '#0d1117'
          }}>
            <div style={{ color: '#8b949e', fontSize: '12px', fontWeight: '600', marginBottom: '10px', textTransform: 'uppercase' }}>
              Available Slides
            </div>
            {loading && (
              <div style={{ color: '#8b949e', padding: '20px 0', textAlign: 'center', fontSize: '14px' }}>
                Loading...
              </div>
            )}
            {error && (
              <div style={{ color: '#f85149', padding: '20px 0', textAlign: 'center', fontSize: '13px' }}>
                {error}
              </div>
            )}
            {(pdfFiles || []).map((pdf, idx) => (
              <button
                key={idx}
                onClick={() => { setSelectedPdf(pdf); setCurrentPage(1); setNumPages(null); }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: '6px',
                  backgroundColor: selectedPdf?.file === pdf.file ? '#163a2a' : '#161b22',
                  color: selectedPdf?.file === pdf.file ? '#86efac' : '#c9d1d9',
                  border: selectedPdf?.file === pdf.file ? '1px solid #86efac' : '1px solid #30363d',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: selectedPdf?.file === pdf.file ? '600' : '400',
                  textAlign: 'left',
                  transition: 'all 0.15s ease'
                }}
              >
                📄 {pdf.name}
              </button>
            ))}
          </div>

          {/* Right side - PDF viewer */}
          <div ref={containerRef} style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#2d2d2d',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {isDemoMode() ? (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 40px',
                textAlign: 'center',
                maxWidth: '600px',
                margin: '0 auto',
              }}>
                <div style={{ fontSize: '56px', marginBottom: '24px' }}>📑</div>
                <h3 style={{ color: '#86efac', fontSize: '22px', fontWeight: '700', marginBottom: '16px' }}>
                  Your Course Slides Will Appear Here
                </h3>
                <p style={{ color: '#c9d1d9', fontSize: '15px', lineHeight: '1.8', marginBottom: '32px' }}>
                  With a real account, your instructor's slides are displayed page by page right here — fully readable in your browser with no downloads needed.
                </p>
                <div style={{
                  backgroundColor: '#161b22',
                  border: '1px solid rgba(134, 239, 172, 0.25)',
                  borderRadius: '12px',
                  padding: '24px 28px',
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: '28px',
                }}>
                  <div style={{ color: '#86efac', fontWeight: '700', fontSize: '14px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    🤖 What the AI can do with each slide
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>💡</span>
                      <div>
                        <div style={{ color: '#e6edf3', fontWeight: '600', fontSize: '14px' }}>Plain-English Explanation</div>
                        <div style={{ color: '#8b949e', fontSize: '13px', marginTop: '3px' }}>The AI reads the slide and explains every concept in simple terms — with analogies and real examples.</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>🐍</span>
                      <div>
                        <div style={{ color: '#e6edf3', fontWeight: '600', fontSize: '14px' }}>Live Code Examples</div>
                        <div style={{ color: '#8b949e', fontSize: '13px', marginTop: '3px' }}>Code snippets from the explanation come with a "Try It" button — run them live in your browser.</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>📝</span>
                      <div>
                        <div style={{ color: '#e6edf3', fontWeight: '600', fontSize: '14px' }}>3 Quiz Questions per Slide</div>
                        <div style={{ color: '#8b949e', fontSize: '13px', marginTop: '3px' }}>After the explanation, get 3 multiple-choice questions with instant feedback and answer explanations.</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>📓</span>
                      <div>
                        <div style={{ color: '#e6edf3', fontWeight: '600', fontSize: '14px' }}>Personal Notes per Slide</div>
                        <div style={{ color: '#8b949e', fontSize: '13px', marginTop: '3px' }}>Add your own notes to any slide — saved automatically and visible every time you return.</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ color: '#8b949e', fontSize: '13px' }}>
                  ✨ <strong style={{ color: '#ffd700' }}>Sign up</strong> to unlock slides, AI explanations, quizzes, and more.
                </div>
              </div>
            ) : pdfUrl ? (
              <>
                {/* Page navigation - Top */}
                <div style={{
                  padding: '12px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  flexShrink: 0,
                  backgroundColor: '#161b22',
                  width: '100%',
                  borderBottom: '1px solid #30363d'
                }}>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    style={{
                      padding: '6px 14px',
                      backgroundColor: currentPage <= 1 ? '#21262d' : '#30363d',
                      color: currentPage <= 1 ? '#484f58' : '#c9d1d9',
                      border: '1px solid rgba(254,240,138,0.3)',
                      borderRadius: '6px',
                      cursor: currentPage <= 1 ? 'default' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    ◀ Prev
                  </button>
                  <span style={{ color: '#e6edf3', fontSize: '15px', fontWeight: '500' }}>
                    Page {currentPage} of {numPages || '...'}
                  </span>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= numPages}
                    style={{
                      padding: '6px 14px',
                      backgroundColor: currentPage >= numPages ? '#21262d' : '#30363d',
                      color: currentPage >= numPages ? '#484f58' : '#c9d1d9',
                      border: '1px solid rgba(254,240,138,0.3)',
                      borderRadius: '6px',
                      cursor: currentPage >= numPages ? 'default' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Next ▶
                  </button>

                  {/* Covered marker */}
                  <div style={{ marginLeft: '20px', borderLeft: '1px solid #30363d', paddingLeft: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                      onClick={() => toggleCovered(selectedPdf.file, currentPage)}
                      style={{
                        padding: '8px 18px',
                        backgroundColor: isPageCovered(selectedPdf.file, currentPage) ? '#16a34a' : '#21262d',
                        color: isPageCovered(selectedPdf.file, currentPage) ? '#fff' : '#fef08a',
                        border: isPageCovered(selectedPdf.file, currentPage) ? '1px solid #22c55e' : '1px solid rgba(254,240,138,0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '700',
                        letterSpacing: '0.3px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {isPageCovered(selectedPdf.file, currentPage) ? '✅ Covered in Class' : '○ Mark as Covered'}
                    </button>

                    <button
                      onClick={() => setShowScratchPad(!showScratchPad)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: showScratchPad ? '#16a34a' : '#21262d',
                        color: showScratchPad ? '#fff' : '#86efac',
                        border: showScratchPad ? '1px solid #22c55e' : '1px solid rgba(254,240,138,0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      💻 Scratch Pad
                    </button>

                    <button
                      onClick={() => setShowTurtle(true)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#21262d',
                        color: '#4ade80',
                        border: '1px solid rgba(74,222,128,0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      🐢 Turtle
                    </button>

                    <button
                      onClick={() => setShowMemo(!showMemo)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: showMemo ? '#0369a1' : '#21262d',
                        color: showMemo ? '#fff' : '#7dd3fc',
                        border: showMemo ? '1px solid #0ea5e9' : '1px solid rgba(254,240,138,0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      📝 Memo
                    </button>

                    <button
                      onClick={handleExplainSlide}
                      disabled={explainLoading}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: showExplain ? '#b45309' : '#21262d',
                        color: showExplain ? '#fff' : '#fbbf24',
                        border: showExplain ? '1px solid #f59e0b' : '1px solid rgba(254,240,138,0.3)',
                        borderRadius: '6px',
                        cursor: explainLoading ? 'wait' : 'pointer',
                        fontSize: '15px',
                        fontWeight: '600',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {explainLoading ? '⏳ Explaining...' : '💬 Explain'}
                    </button>
                  </div>
                </div>

                {/* PDF Document */}
                <div style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}>
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div style={{ color: '#8b949e', padding: '60px', fontSize: '16px' }}>
                        Loading PDF...
                      </div>
                    }
                    error={
                      <div style={{ color: '#f85149', padding: '60px', fontSize: '16px' }}>
                        Failed to load PDF
                      </div>
                    }
                  >
                    <Page
                      pageNumber={currentPage}
                      width={Math.min(containerWidth, 1000)}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                  </Document>
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#8b949e',
                fontSize: '16px'
              }}>
                {loading ? 'Loading slides...' : 'Select a slide to view'}
              </div>
            )}
            )}
          </div>
        </div>
      </div>

      {/* ===== Scratch Pad (CodeRunnerModal) ===== */}
      {showScratchPad && (
        <CodeRunnerModal
          title="Scratch Pad"
          initialCode=""
          onClose={() => setShowScratchPad(false)}
        />
      )}
      {/* ===== Floating Memo Pad ===== */}
      {showMemo && (
        <div style={{
          position: 'fixed',
          left: memoPos.x,
          top: memoPos.y,
          width: memoSize.width,
          height: memoSize.height,
          backgroundColor: '#0c1929',
          border: '2px solid #0ea5e9',
          borderRadius: '12px',
          boxShadow: '0 12px 40px rgba(14,165,233,0.3)',
          zIndex: 10002,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header (draggable) */}
          <div
            onMouseDown={(e) => {
              memoDragging.current = true;
              memoDragOffset.current = { x: e.clientX - memoPos.x, y: e.clientY - memoPos.y };
              e.preventDefault();
            }}
            style={{
              padding: '10px 14px',
              backgroundColor: '#0c2a4a',
              borderBottom: '1px solid #0ea5e9',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'grab',
              userSelect: 'none',
              flexShrink: 0
            }}
          >
            <span style={{ color: '#7dd3fc', fontSize: '15px', fontWeight: '700' }}>
              📝 Memo — {selectedPdf?.name || 'Slide'} p{currentPage}
            </span>
            <button
              onClick={() => setShowMemo(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f0f6fc',
                fontSize: '22px',
                cursor: 'pointer',
                fontWeight: 'bold',
                padding: '0 4px'
              }}
              onMouseOver={(e) => e.target.style.color = '#f85149'}
              onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
            >✕</button>
          </div>

          {/* Notes textarea */}
          <textarea
            value={memoText}
            onChange={(e) => setMemoText(e.target.value)}
            placeholder="Write your notes for this slide page...&#10;Notes are auto-saved per slide page."
            style={{
              flex: 1,
              backgroundColor: '#081525',
              color: '#bae6fd',
              border: 'none',
              padding: '14px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '1.6',
              resize: 'none',
              outline: 'none',
              caretColor: '#38bdf8'
            }}
          />

          {/* Footer with char count */}
          <div style={{
            padding: '6px 14px',
            borderTop: '1px solid #164e63',
            backgroundColor: '#0c2a4a',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <span style={{ color: '#6b7280', fontSize: '11px' }}>
              Auto-saved • {memoText.length} chars
            </span>
            <button
              onClick={() => {
                const chapterName = (selectedPdf?.name || 'slide').replace(/\s+/g, '_');
                const fileName = `memo_${chapterName}_page${currentPage}.txt`;
                const blob = new Blob([memoText], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(url);
              }}
              disabled={!memoText.trim()}
              style={{
                padding: '2px 10px',
                backgroundColor: !memoText.trim() ? '#21262d' : '#0369a1',
                color: !memoText.trim() ? '#484f58' : '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: !memoText.trim() ? 'default' : 'pointer',
                fontSize: '11px',
                fontWeight: '600'
              }}
            >
              💾 Download
            </button>
          </div>

          {/* Resize handle */}
          <div
            onMouseDown={(e) => {
              memoResizing.current = true;
              memoDragOffset.current = { x: e.clientX, y: e.clientY, width: memoSize.width, height: memoSize.height };
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '18px',
              height: '18px',
              cursor: 'nwse-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0ea5e9',
              fontSize: '12px',
              userSelect: 'none'
            }}
          >⬔</div>
        </div>
      )}

      {/* ===== Floating Explain Panel ===== */}
      {showExplain && (
        <div style={{
          position: 'fixed',
          left: explainPos.x,
          top: explainPos.y,
          width: explainSize.width,
          height: explainSize.height,
          backgroundColor: '#1a1508',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          boxShadow: '0 12px 40px rgba(245,158,11,0.3)',
          zIndex: 10002,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header (draggable) */}
          <div
            onMouseDown={(e) => {
              explainDragging.current = true;
              explainDragOffset.current = { x: e.clientX - explainPos.x, y: e.clientY - explainPos.y };
              e.preventDefault();
            }}
            style={{
              padding: '10px 14px',
              backgroundColor: '#2a1f0a',
              borderBottom: '1px solid #f59e0b',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'grab',
              userSelect: 'none',
              flexShrink: 0
            }}
          >
            <span style={{ color: '#fbbf24', fontSize: '15px', fontWeight: '700' }}>
              💬 Explain — {selectedPdf?.name || 'Slide'} p{currentPage}
            </span>
            <button
              onClick={() => setShowExplain(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#f0f6fc',
                fontSize: '22px',
                cursor: 'pointer',
                fontWeight: 'bold',
                padding: '0 4px'
              }}
              onMouseOver={(e) => e.target.style.color = '#f85149'}
              onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
            >✕</button>
          </div>

          {/* Explanation content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 22px',
            backgroundColor: '#1a1708',
            color: '#ffffff',
            fontSize: '16.5px',
            lineHeight: '1.9',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
            letterSpacing: '0.3px',
            fontWeight: '450'
          }}>
            {explainLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#fbbf24' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #3d2e06',
                  borderTop: '4px solid #fbbf24',
                  borderRadius: '50%',
                  margin: '0 auto 16px auto',
                  animation: 'explainSpin 1s linear infinite'
                }} />
                <style>{`@keyframes explainSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                <div style={{ fontSize: '15px', fontWeight: '600' }}>Analyzing slide content...</div>
              </div>
            ) : (
              <>
                {explainContent || <span style={{ color: '#8b949e' }}>Click "Explain" to get an AI explanation of this slide.</span>}

                {/* Quiz Questions */}
                {explainQuiz.length > 0 && (
                  <div style={{
                    marginTop: '28px',
                    borderTop: '2px solid #f59e0b',
                    paddingTop: '20px'
                  }}>
                    <div style={{
                      fontSize: '17px',
                      fontWeight: '700',
                      color: '#fbbf24',
                      marginBottom: '18px'
                    }}>🧠 Quick Quiz</div>
                    {explainQuiz.map((q, qi) => {
                      const answered = quizAnswers.hasOwnProperty(qi);
                      const chosenIdx = quizAnswers[qi];
                      const isCorrect = answered && chosenIdx === q.correct;
                      return (
                        <div key={qi} style={{
                          marginBottom: '22px',
                          padding: '14px 16px',
                          backgroundColor: '#0d1117',
                          borderRadius: '10px',
                          border: answered
                            ? (isCorrect ? '1px solid #22c55e' : '1px solid #ef4444')
                            : '1px solid #30363d'
                        }}>
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            color: '#e5e7eb',
                            marginBottom: '12px',
                            lineHeight: '1.5'
                          }}>
                            {qi + 1}. {q.question}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {q.options.map((opt, oi) => {
                              const isChosen = answered && chosenIdx === oi;
                              const isCorrectOption = answered && q.correct === oi;
                              let optBg = '#161b22';
                              let optBorder = '1px solid #30363d';
                              let optColor = '#c9d1d9';
                              if (answered) {
                                if (isCorrectOption) {
                                  optBg = 'rgba(34,197,94,0.15)';
                                  optBorder = '1px solid #22c55e';
                                  optColor = '#86efac';
                                } else if (isChosen) {
                                  optBg = 'rgba(239,68,68,0.15)';
                                  optBorder = '1px solid #ef4444';
                                  optColor = '#fca5a5';
                                }
                              }
                              return (
                                <button
                                  key={oi}
                                  onClick={() => {
                                    if (!answered) {
                                      setQuizAnswers(prev => ({ ...prev, [qi]: oi }));
                                    }
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 14px',
                                    backgroundColor: optBg,
                                    border: optBorder,
                                    borderRadius: '8px',
                                    color: optColor,
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    cursor: answered ? 'default' : 'pointer',
                                    textAlign: 'left',
                                    fontFamily: 'inherit',
                                    transition: 'all 0.15s ease',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                  onMouseOver={(e) => {
                                    if (!answered) {
                                      e.currentTarget.style.backgroundColor = '#1c2333';
                                      e.currentTarget.style.borderColor = '#f59e0b';
                                    }
                                  }}
                                  onMouseOut={(e) => {
                                    if (!answered) {
                                      e.currentTarget.style.backgroundColor = '#161b22';
                                      e.currentTarget.style.borderColor = '#30363d';
                                    }
                                  }}
                                >
                                  {answered && isCorrectOption && <span>✅</span>}
                                  {answered && isChosen && !isCorrectOption && <span>❌</span>}
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                          {answered && (
                            <div style={{
                              marginTop: '10px',
                              padding: '10px 12px',
                              backgroundColor: isCorrect ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                              borderRadius: '6px',
                              fontSize: '13px',
                              lineHeight: '1.6',
                              color: isCorrect ? '#86efac' : '#fca5a5'
                            }}>
                              {isCorrect ? '🎉 Correct! ' : '💡 Not quite. '}
                              {q.explanation}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '6px 14px',
            borderTop: '1px solid #3d2e06',
            backgroundColor: '#2a1f0a',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <span></span>
            <button
              onClick={() => {
                // Clear cache for this page so it fetches fresh
                const cacheKey = `${selectedPdf.file}-p${currentPage}`;
                delete explainCache.current[cacheKey];
                handleExplainSlide();
              }}
              disabled={explainLoading}
              style={{
                padding: '3px 10px',
                backgroundColor: explainLoading ? '#21262d' : '#b45309',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: explainLoading ? 'not-allowed' : 'pointer',
                fontSize: '11px',
                fontWeight: '600'
              }}
            >
              🔄 Re-explain
            </button>
          </div>

          {/* Resize handle */}
          <div
            onMouseDown={(e) => {
              explainResizing.current = true;
              explainDragOffset.current = { x: e.clientX, y: e.clientY, width: explainSize.width, height: explainSize.height };
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '18px',
              height: '18px',
              cursor: 'nwse-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#f59e0b',
              fontSize: '12px',
              userSelect: 'none'
            }}
          >⬔</div>
        </div>
      )}

      {/* Turtle Graphics Modal */}
      {showTurtle && (
        <TurtleModal onClose={() => setShowTurtle(false)} />
      )}
    </div>
  );
};

export default CourseSlidesPanel;
